'use client';
import { API_BASE_URL } from '@/lib/config';


import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

export default function LoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [ssoEmail, setSsoEmail] = useState('');
    const [ssoLoading, setSsoLoading] = useState(false);
    const [showSso, setShowSso] = useState(false);
    const router = useRouter();

    const handleSsoLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSsoLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/auth/sso-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: ssoEmail.trim() }),
            });
            const data = await response.json();
            if (response.ok && data.token) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('userId', data.user.id);
                localStorage.setItem('username', data.user.username);
                router.push('/');
            } else {
                setError(data.error || 'SSO login failed');
            }
        } catch (err) {
            setError('Network error. Is the API running?');
        } finally {
            setSsoLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // VULNERABILITY: Calling API without HTTPS
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: username.trim(),
                    password: password.trim()
                }),
            });

            const data = await response.json();

            if (response.ok && data.token) {
                // VULNERABILITY: Storing sensitive data in localStorage (not encrypted)
                // Store token and user for Navbar detection
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));

                // Also store individual fields for backward compatibility
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('userId', data.user.id);
                localStorage.setItem('username', data.user.username);
                localStorage.setItem('isAdmin', data.user.is_admin);

                // VULNERABILITY: Also storing password (NEVER do this!)
                localStorage.setItem('lastPassword', password);

                // VULNERABILITY: Open redirect - no validation of return URL
                const returnUrl = new URLSearchParams(window.location.search).get('return') ||
                    new URLSearchParams(window.location.search).get('redirect');
                if (returnUrl) {
                    // Dangerous - can redirect to any URL!
                    window.location.href = returnUrl;
                } else {
                    router.push('/');
                }
            } else {
                setError(data.error || 'Login failed');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-4">
            <div className="w-full max-w-md">
                <div className="glass-card p-8">
                    <h1 className="text-3xl font-bold text-center mb-8 text-gradient-primary">
                        Login to VulneraShop
                    </h1>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
                            <p className="text-sm text-red-300">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="username" className="block text-sm font-medium text-neutral-300 mb-2">
                                Username
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full px-4 py-3 rounded-lg bg-neutral-900 border border-neutral-800 text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition"
                                placeholder="Your username"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-neutral-300 mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-lg bg-neutral-900 border border-neutral-800 text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition"
                                placeholder="Your password"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                    </form>

                    <div className="mt-6">
                        <div className="relative flex items-center my-4">
                            <div className="flex-1 border-t border-neutral-800"></div>
                            <span className="px-3 text-xs uppercase tracking-wider text-neutral-500">or</span>
                            <div className="flex-1 border-t border-neutral-800"></div>
                        </div>

                        {!showSso ? (
                            <button
                                type="button"
                                onClick={() => setShowSso(true)}
                                className="w-full py-3 rounded-lg bg-neutral-900 border border-neutral-800 text-white hover:border-orange-500 transition"
                            >
                                Continue with SSO
                            </button>
                        ) : (
                            <form onSubmit={handleSsoLogin} className="space-y-3">
                                <input
                                    id="sso-email"
                                    type="email"
                                    value={ssoEmail}
                                    onChange={(e) => setSsoEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg bg-neutral-900 border border-neutral-800 text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition"
                                    placeholder="you@company.com"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={ssoLoading}
                                    className="btn-secondary w-full py-3 disabled:opacity-50"
                                >
                                    {ssoLoading ? 'Signing in...' : 'Sign in with SSO'}
                                </button>
                            </form>
                        )}
                    </div>

                    <p className="text-center text-neutral-500 text-sm mt-6">
                        Don't have an account?{' '}
                        <Link href="/register" className="text-orange-400 hover:text-orange-300 font-medium">Sign up</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
